import { CardPriority } from '..';
import { NoteFormat, NoteIntegration, NotePriority } from '../../../enums';
import { NoteTypeData } from '../../../data/task-manager';
import { useNotesContext } from '../../../hooks/useNotesContext';
import { TNote } from '../../../types';
import { getAssignee } from '../../../utils';
import { NoteCardProps } from '../types';
import { NoteCard } from './card';

type Props = {
  note: TNote;
} & Partial<NoteCardProps>;

/**
 * A component that maps a note object to a NoteCard component with proper formatting and handlers.
 * Provides functionality for displaying note details, managing note status, and handling note actions.
 *
 * @param {Props} props - Component props
 * @param {TNote} props.note - The note object containing all note data
 * @param {Partial<NoteCardProps>} props.rest - Additional props to pass to the NoteCard component
 * @returns {JSX.Element} A rendered NoteCard component with mapped note data
 */
export function MappedNoteCard({ note, onEdit, ...props }: Props) {
  const { handleMoveToNote, setCurrentAction, tags, integration, collaborators } =
    useNotesContext();
  const noteTag = tags.find((t) => t.id === note.tagId);
  const description = note.messages.find((m) => m.isMainMessage)?.description;
  const messagesCount = note.messages.filter((m) => !m.isMainMessage).length ?? 0;

  const CardNotePriorityMap: Record<NotePriority, CardPriority> = {
    [NotePriority.Highest]: CardPriority.Highest,
    [NotePriority.High]: CardPriority.High,
    [NotePriority.Medium]: CardPriority.Medium,
    [NotePriority.Low]: CardPriority.Low,
    [NotePriority.Lower]: CardPriority.Lower,
    [NotePriority.Lowest]: CardPriority.Lowest,
  } as const;

  const fromDate =
    integration === NoteIntegration.ICL && note?.notesICLData?.availableFrom
      ? new Date(note.notesICLData.availableFrom) ?? new Date(note.createdAt)
      : new Date(note.createdAt);

  return (
    <NoteCard
      id={note.id.toString()}
      title={note.title}
      description={description}
      isCompleted={note.isCompleted}
      reporter={note.reporter ? { name: note.reporter } : null}
      assignee={note.assignee ? getAssignee(note.assignee, collaborators) : null}
      secondaryTitle={noteTag?.title}
      secondaryDescription={noteTag?.description}
      indicatorColor={NoteTypeData[note.type]?.color}
      indicatorStyle="side"
      hasNewMessages={messagesCount > 1}
      priority={CardNotePriorityMap[note.priority]}
      fromDate={fromDate}
      toDate={note.dueDate ? new Date(note.dueDate) : null}
      onMoveToTagClick={() => {
        handleMoveToNote(note);
      }}
      onStatusChange={() => {
        setCurrentAction({ type: 'change-status', note });
      }}
      onDelete={() => {
        setCurrentAction?.({ type: 'delete', note });
      }}
      onEdit={note.format === NoteFormat.Bazan ? null : onEdit}
      {...props}
    />
  );
}
